import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ProductGallery from '../components/ProductGallery';
import { PageSeo } from '../components/Seo';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { canAddToCart, canPreOrder, getPrimaryImage, getProductUrl } from '../utils/product';
import { formatPrice } from '../utils/formatPrice';
import { breadcrumbJsonLd, productJsonLd } from '../utils/structuredData';

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getProductBySlug, loadProductBySlug, isLoading } = useProducts();
  const { addToCart, items } = useCart();
  const [quantity, setQuantity] = useState(1);
  const resolvedProduct = slug ? getProductBySlug(slug) : undefined;
  const [ready, setReady] = useState(Boolean(resolvedProduct));

  useEffect(() => {
    if (!slug) {
      setReady(true);
      return;
    }

    if (getProductBySlug(slug)) {
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);

    void loadProductBySlug(slug).finally(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [slug, getProductBySlug, loadProductBySlug]);

  const inCart = resolvedProduct
    ? (items.find((i) => i.product.id === resolvedProduct.id)?.quantity ?? 0)
    : 0;
  const maxQuantity = resolvedProduct ? Math.max(0, resolvedProduct.stock - inCart) : 0;

  if (!ready || (isLoading && !resolvedProduct)) {
    return (
      <div className="container empty-state">
        <p>Ürün yükleniyor...</p>
      </div>
    );
  }

  if (!resolvedProduct) {
    return (
      <div className="container empty-state">
        <PageSeo title="Ürün Bulunamadı" description="Aradığınız ürün bulunamadı." noindex />
        <h1>Ürün bulunamadı</h1>
        <Link to="/shop" className="btn btn-primary">Ürünlere Dön</Link>
      </div>
    );
  }

  const addable = canAddToCart(resolvedProduct);
  const productPath = getProductUrl(resolvedProduct.slug);

  const handleAddToCart = () => {
    addToCart(resolvedProduct, quantity);
    navigate('/cart');
  };

  const productImage = getPrimaryImage(resolvedProduct);

  return (
    <div className="container product-detail">
      <PageSeo
        title={resolvedProduct.name}
        description={resolvedProduct.description}
        path={productPath}
        image={productImage}
        type="product"
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Ana Sayfa', path: '/' },
            { name: 'Ürünler', path: '/shop' },
            { name: resolvedProduct.name, path: productPath },
          ]),
          productJsonLd(resolvedProduct),
        ]}
      />
      <Link to="/shop" className="back-link">
        <ArrowLeft size={16} aria-hidden />
        Ürünlere Dön
      </Link>

      <div className="product-detail-grid">
        <div className="product-detail-image">
          <ProductGallery images={resolvedProduct.images} alt={resolvedProduct.name} />
          {!resolvedProduct.inStock && <span className="out-of-stock-badge">Stokta Yok</span>}
        </div>

        <div className="product-detail-info">
          <span className="product-category">{resolvedProduct.category}</span>
          <h1>{resolvedProduct.name}</h1>
          <p className="product-detail-price">{formatPrice(resolvedProduct)}</p>
          <p className={`stock-info ${resolvedProduct.stock <= 5 && resolvedProduct.inStock ? 'stock-low' : ''}`}>
            {resolvedProduct.inStock ? `Stok: ${resolvedProduct.stock} adet` : 'Stokta yok'}
          </p>

          <p className="product-detail-desc">{resolvedProduct.description}</p>

          <div className="benefits">
            <h3>Öne Çıkan Özellikler</h3>
            <ul>
              {resolvedProduct.benefits.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>
          </div>

          <div className="product-detail-purchase">
            <div className="product-detail-purchase-row">
              {addable && maxQuantity > 0 && (
                <div className="quantity-selector">
                  <label htmlFor="quantity">Adet:</label>
                  <div className="quantity-controls">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      aria-label="Azalt"
                      disabled={quantity <= 1}
                    >
                      −
                    </button>
                    <span id="quantity">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                      aria-label="Artır"
                      disabled={quantity >= maxQuantity}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {!resolvedProduct.inStock ? (
                canPreOrder(resolvedProduct) ? (
                  <Link to={`/on-siparis/${resolvedProduct.slug}`} className="btn btn-primary btn-lg">
                    Ön Sipariş Oluştur
                  </Link>
                ) : (
                  <button type="button" className="btn btn-primary btn-lg" disabled>
                    Stokta Yok
                  </button>
                )
              ) : addable && maxQuantity > 0 ? (
                <button type="button" className="btn btn-primary btn-lg" onClick={handleAddToCart}>
                  Sepete Ekle
                </button>
              ) : addable && maxQuantity === 0 ? (
                <button type="button" className="btn btn-primary btn-lg" disabled>
                  Stok Limitine Ulaşıldı
                </button>
              ) : resolvedProduct.priceLabel ? (
                <a href="tel:+905535382462" className="btn btn-primary btn-lg">
                  Sipariş Ver — +90 553 538 24 62
                </a>
              ) : (
                <button type="button" className="btn btn-primary btn-lg" disabled>
                  Stokta Yok
                </button>
              )}
            </div>

            {addable && maxQuantity > 0 && inCart > 0 && (
              <span className="form-hint product-detail-purchase-hint">
                Sepette {inCart} adet var (max {maxQuantity})
              </span>
            )}
          </div>

          {resolvedProduct.specs && (
            <div className="product-specs">
              <h3>Ürün Özellikleri</h3>
              <table>
                <tbody>
                  {Object.entries(resolvedProduct.specs).map(([key, value]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {resolvedProduct.usageWarning && (
            <div className="product-usage-warning" role="note">
              <h3>Kullanım Uyarısı</h3>
              <p>{resolvedProduct.usageWarning}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
